"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = LazyVideo;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _reactIntersectionObserver = require("react-intersection-observer");
var _patternLibraryUtils = require("@site/pattern-library-utils");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/* eslint-disable jsx-a11y/media-has-caption */
/**
 * LazyVideo
 *
 * @param {Object} props props
 * @param {String} props.id Video Identifier
 * @param {Object} props.videoSource Collection of urls for the various video formats and fallback poster image.
 * Adding mp4 as fallback
 * is because historially, different browsers have supported different video formats.
 * Webm is preferred as a first line
 * because it tends to have better compression without sacrificing quality. As of the latest updates,
 * all browsers support webm. Source: https://caniuse.com/?search=webm
 * @param {String} props.title Title text
 * @param {boolean} props.playsInline the video is to be played "inline", that is within the element's
 * playback area.
 * @param {boolean} props.autoPlay the video automatically begins to play back as soon as it can do so without
 * stopping to finish loading the data.
 * @param {boolean} props.muted indicates the default setting of the audio contained in the video.
 * @param {boolean} props.loop the browser will automatically seek back to the start upon reaching the end of the video.
 * @param {string} props.className - Class names passed for styled linaria component
 * @param {object} props.style - Style props passed for styled linaria component
 * @returns {React.ReactFragment} LazyVideo Component
 * @public
 */

// eslint-disable-next-line complexity
function LazyVideo({
  id,
  className,
  style,
  videoSource,
  title,
  pause = false,
  preload = 'none',
  playsInline = true,
  autoPlay = true,
  muted = true,
  loop = false,
  restartOnPause = false,
  onEnded
}) {
  const {
    fallback,
    fallbackMobile,
    laptop,
    laptopWebm,
    base,
    baseWebm
  } = videoSource;
  const videoRef = (0, _react.useRef)(null);
  const [hasBeenInView, setHasBeenInView] = (0, _react.useState)(false);
  const {
    width
  } = (0, _patternLibraryUtils.useWindowSize)();
  const {
    ref: inViewRef,
    inView
  } = (0, _reactIntersectionObserver.useInView)({
    triggerOnce: true,
    rootMargin: '200px 0px',
    fallbackInView: true
  });
  const setCombinedRef = element => {
    videoRef.current = element;
    if (typeof inViewRef === 'function') {
      inViewRef(element);
    } else if (inViewRef && 'current' in inViewRef) {
      inViewRef.current = element;
    }
  };
  const getFallbackUrl = () => {
    if (width <= _patternLibraryUtils.size.mobileLg && fallbackMobile) {
      return fallbackMobile;
    }
    return fallback;
  };
  (0, _react.useEffect)(() => {
    if (inView && !hasBeenInView) {
      setHasBeenInView(true);
    }
  }, [inView]);
  (0, _react.useEffect)(() => {
    const videoElement = videoRef?.current;
    if (videoElement) {
      if (pause) {
        videoElement.pause();
      } else if (hasBeenInView) {
        videoElement.play();
      }
    }
  }, [pause, hasBeenInView]);
  (0, _react.useEffect)(() => {
    if (pause && restartOnPause) {
      videoRef.current.currentTime = 0;
    }
  }, [pause, restartOnPause]);
  const posterUrl = getFallbackUrl();
  const videoPosterAttr = hasBeenInView ? {
    poster: posterUrl
  } : {
    'data-poster': posterUrl
  };
  return /*#__PURE__*/_react.default.createElement("video", (0, _extends2.default)({
    id: id,
    ref: setCombinedRef,
    title: title,
    preload: preload,
    width: '100%',
    height: '100%',
    playsInline: playsInline,
    autoPlay: pause ? false : autoPlay && hasBeenInView,
    muted: muted,
    loop: loop,
    onEnded: onEnded,
    "data-cy": "lazy-video",
    style: style,
    className: className
  }, videoPosterAttr), laptopWebm && width >= _patternLibraryUtils.size.mobileLg && hasBeenInView && /*#__PURE__*/_react.default.createElement("source", {
    src: laptopWebm,
    type: "video/webm",
    media: `all and ${_patternLibraryUtils.device.mobileLg}`
  }), baseWebm && (!laptopWebm && !laptop || laptopWebm && width <= _patternLibraryUtils.size.mobileLg) && hasBeenInView && /*#__PURE__*/_react.default.createElement("source", {
    src: baseWebm,
    type: "video/webm"
  }), laptop && width >= _patternLibraryUtils.size.mobileLg && hasBeenInView && /*#__PURE__*/_react.default.createElement("source", {
    src: laptop,
    type: "video/mp4",
    media: `all and ${_patternLibraryUtils.device.mobileLg}`
  }), base && (!laptop && !laptopWebm || laptop && width <= _patternLibraryUtils.size.mobileLg) && hasBeenInView && /*#__PURE__*/_react.default.createElement("source", {
    src: base,
    type: "video/mp4"
  }));
}
LazyVideo.propTypes = {
  id: _propTypes.default.string,
  videoSource: _propTypes.default.shape({
    fallback: _propTypes.default.string,
    fallbackMobile: _propTypes.default.string,
    laptop: _propTypes.default.string,
    laptopWebm: _propTypes.default.string,
    base: _propTypes.default.string,
    baseWebm: _propTypes.default.string
  }).isRequired,
  className: _propTypes.default.string,
  style: _propTypes.default.object,
  title: _propTypes.default.string,
  pause: _propTypes.default.bool,
  preload: _propTypes.default.oneOf(['none', 'metadata', 'auto']),
  playsInline: _propTypes.default.bool,
  autoPlay: _propTypes.default.bool,
  muted: _propTypes.default.bool,
  loop: _propTypes.default.bool,
  restartOnPause: _propTypes.default.bool,
  onEnded: _propTypes.default.func
};